/**
 * Learner global search: opens from bottom banner; animated panel; server-backed search.
 */

import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, SlidersHorizontal, Loader2, ListFilter, MapPin, Lock, Bookmark } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  contentService,
  LearnerSearchRequest,
  LearnerSearchResult
} from '../../services/contentService';
import { FarsiKeyboardInput } from './FarsiKeyboardInput';
import { ExerciseLockedModal, LockedContentKind } from './ExerciseLockedModal';
import { mobilePinnedAudioChromeBottom } from '../../utils/audioBarChromeState';
import { ModalTourHelpButton } from '../tour/ModalTourHelpButton';
import {
  TOUR_TARGET_FOCUS_EVENT,
  type TourTargetEventDetail,
} from '../../learnerTour/tourTargetEvents';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';
import { LEARNER_UI_ANIM_MS } from '../../utils/learnerSlideModalMotion';

export interface LearningSearchScopePayload {
  courseId?: number;
  levelId?: number;
  lessonId?: number;
  /** Book reader — scope "only search where I am now" to this book. */
  libraryBookId?: number;
  /** Library catalog page — scope to published library books only. */
  limitToLibrary?: boolean;
}

export interface LearnerSearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
  /** Text+audio bar pinned to the viewport bottom (not in-flow at block end). */
  audioBarPinned: boolean;
  /** Extra height (px) stacked above the text+audio bar (e.g. waveform). */
  audioBarExtraChromePx?: number;
  bannerCollapsed: boolean;
  /** When true, show “limit to this screen” using scope ids below. */
  canLimitToLearningContext: boolean;
  learningScope: LearningSearchScopePayload;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightText(text: string, query: string): React.ReactNode {
  const source = typeof text === 'string' ? text : '';
  const q = query.trim().normalize('NFC');
  if (!q || !source) return source;
  const normalizedText = source.normalize('NFC');
  const parts = normalizedText.split(new RegExp(`(${escapeRegex(q)})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === q.toLowerCase() ? (
      <strong key={i} className="font-semibold text-gray-900 dark:text-gray-100">
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function matchKindLabel(kind: string, t: (key: string) => string): string {
  switch (kind) {
    case 'Title':
      return t('matchKindTitle');
    case 'Text':
      return t('matchKindLessonText');
    case 'Quiz':
      return t('matchKindQuiz');
    case 'Exercise':
      return t('matchKindExercise');
    case 'CourseCatalog':
      return t('matchKindCourseDescription');
    case 'LevelCatalog':
      return t('matchKindLevelDescription');
    case 'LibraryTitle':
      return t('matchKindBookTitle');
    case 'LibraryText':
      return t('matchKindBookText');
    case 'Vocabulary':
      return t('matchKindVocabulary');
    case 'Leitner':
      return t('matchKindLeitner');
    default:
      return kind;
  }
}

function navigateFromResult(r: LearnerSearchResult, term: string): void {
  const highlightTermTrim = term.trim();
  const highlightTerm = highlightTermTrim.length > 0 ? highlightTermTrim : undefined;

  if (r.matchKind === 'Leitner' || (r.leitnerCardId != null && r.leitnerCardId > 0)) {
    window.dispatchEvent(
      new CustomEvent('app:openLeitner', {
        detail: { cardId: r.leitnerCardId, highlightTerm }
      })
    );
    return;
  }

  if (r.libraryBookId != null && r.libraryBookId > 0) {
    window.dispatchEvent(
      new CustomEvent('app:learnerSearchNavigate', {
        detail: {
          kind: 'book',
          libraryBookId: r.libraryBookId,
          libraryBookBlockId:
            r.libraryBookBlockId != null && r.libraryBookBlockId > 0
              ? r.libraryBookBlockId
              : undefined,
          highlightTerm
        }
      })
    );
    return;
  }

  const hasLesson = r.lessonId != null && r.lessonId > 0;
  const hasBlock = r.contentBlockId != null && r.contentBlockId > 0;
  const hasLevel = r.levelId != null && r.levelId > 0;

  // Lesson exercise hit → open the lesson viewer with the exercise modal popped
  // open on the matched question. The lesson viewer handles auto-opening via
  // `openExerciseId`. The modal flashes the matched question and inline-marks
  // the term inside its body.
  if (hasLesson && r.lessonExerciseId != null && r.lessonExerciseId > 0) {
    window.dispatchEvent(
      new CustomEvent('app:learnerSearchNavigate', {
        detail: {
          kind: 'lesson',
          lessonId: r.lessonId,
          lessonExerciseId: r.lessonExerciseId,
          highlightTerm
        }
      })
    );
    return;
  }

  // Lesson quiz hit → open the lesson viewer with the quiz modal popped open
  // (browse mode – the timer doesn't start until the learner hits Start). The
  // modal jumps to the matched question, flashes its container and inline-
  // marks the term.
  if (
    hasLesson &&
    r.matchKind === 'Quiz' &&
    r.lessonQuizId != null &&
    r.lessonQuizId > 0
  ) {
    window.dispatchEvent(
      new CustomEvent('app:learnerSearchNavigate', {
        detail: {
          kind: 'lesson',
          lessonId: r.lessonId,
          lessonQuizId: r.lessonQuizId,
          openQuizFromSearch: true,
          highlightTerm
        }
      })
    );
    return;
  }

  // Lesson title hit → CourseDetail with the lesson row pulsing.
  // Only the title flavor – block titles / body / tips still drill into the lesson
  // viewer where the matched block is shown directly.
  if (hasLesson && hasLevel && !hasBlock && r.matchKind === 'Title' && r.subKind === 'Lesson') {
    window.dispatchEvent(
      new CustomEvent('app:learnerSearchNavigate', {
        detail: {
          kind: 'level',
          courseId: r.courseId,
          levelId: r.levelId,
          highlightType: 'lesson',
          highlightId: r.lessonId,
          highlightTerm
        }
      })
    );
    return;
  }

  // Block-level lesson hits (title / body text) keep their existing UX:
  // jump straight into the lesson viewer with the right block targeted. The
  // lesson viewer pulses that block and marks the term in its title for the
  // duration of the highlight.
  if (hasLesson) {
    window.dispatchEvent(
      new CustomEvent('app:learnerSearchNavigate', {
        detail: {
          kind: 'lesson',
          lessonId: r.lessonId,
          contentBlockId: hasBlock ? r.contentBlockId : undefined,
          highlightTerm
        }
      })
    );
    return;
  }

  // Level title / description hit → CourseDetail with the level card itself pulsing.
  if (hasLevel) {
    window.dispatchEvent(
      new CustomEvent('app:learnerSearchNavigate', {
        detail: {
          kind: 'level',
          courseId: r.courseId,
          levelId: r.levelId,
          highlightType: 'level',
          highlightId: r.levelId,
          highlightTerm
        }
      })
    );
    return;
  }

  // Course title / description hit → catalog with course card flash.
  window.dispatchEvent(
    new CustomEvent('app:learnerSearchNavigate', {
      detail: {
        kind: 'course',
        courseId: r.courseId,
        highlightTerm
      }
    })
  );
}

export const LearnerSearchPanel: React.FC<LearnerSearchPanelProps> = ({
  isOpen,
  onClose,
  isMobile,
  audioBarPinned,
  audioBarExtraChromePx = 0,
  bannerCollapsed,
  canLimitToLearningContext,
  learningScope
}) => {
  const { t } = useTranslation('search');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<LearnerSearchResult[]>([]);
  /** Active locked-content modal state. Set when the learner clicks a locked hit. */
  const [lockedModal, setLockedModal] = useState<{
    open: boolean;
    title: string;
    kind: LockedContentKind;
    plans: { id: number; name: string; description?: string }[];
  } | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [searchTitles, setSearchTitles] = useState(true);
  const [searchDescriptions, setSearchDescriptions] = useState(true);
  const [searchLessonText, setSearchLessonText] = useState(true);
  const [searchLessonQuizzes, setSearchLessonQuizzes] = useState(true);
  const [searchLessonExercises, setSearchLessonExercises] = useState(true);
  const [searchLibraryBookText, setSearchLibraryBookText] = useState(true);
  const [limitToContext, setLimitToContext] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [presented, setPresented] = useState(false);

  const SEARCH_PANEL_ANIM_MS = LEARNER_UI_ANIM_MS;

  useHistoryBackClose(isOpen, onClose, 'learner-search');

  useLayoutEffect(() => {
    if (isOpen) {
      setMounted(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setPresented(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setPresented(false);
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (presented || !mounted) return undefined;
    const id = window.setTimeout(() => setMounted(false), SEARCH_PANEL_ANIM_MS);
    return () => window.clearTimeout(id);
  }, [presented, mounted]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 320);
    return () => window.clearTimeout(t);
  }, [query]);

  const scopePayload = useMemo((): Pick<
    LearnerSearchRequest,
    | 'limitToLearningContext'
    | 'scopeCourseId'
    | 'scopeLevelId'
    | 'scopeLessonId'
    | 'scopeLimitToLibrary'
    | 'scopeLibraryBookId'
  > => {
    if (!limitToContext || !canLimitToLearningContext) {
      return {
        limitToLearningContext: false,
        scopeCourseId: undefined,
        scopeLevelId: undefined,
        scopeLessonId: undefined,
        scopeLimitToLibrary: false,
        scopeLibraryBookId: undefined
      };
    }
    const { courseId, levelId, lessonId, libraryBookId, limitToLibrary } = learningScope;
    if (libraryBookId != null && libraryBookId > 0) {
      return {
        limitToLearningContext: true,
        scopeLibraryBookId: libraryBookId,
        scopeLimitToLibrary: false,
        scopeCourseId: undefined,
        scopeLevelId: undefined,
        scopeLessonId: undefined
      };
    }
    if (limitToLibrary) {
      return {
        limitToLearningContext: true,
        scopeLimitToLibrary: true,
        scopeLibraryBookId: undefined,
        scopeCourseId: undefined,
        scopeLevelId: undefined,
        scopeLessonId: undefined
      };
    }
    if (lessonId != null && lessonId > 0) {
      return {
        limitToLearningContext: true,
        scopeCourseId: courseId,
        scopeLevelId: levelId,
        scopeLessonId: lessonId,
        scopeLimitToLibrary: false,
        scopeLibraryBookId: undefined
      };
    }
    if (levelId != null && levelId > 0) {
      return {
        limitToLearningContext: true,
        scopeCourseId: courseId,
        scopeLevelId: levelId,
        scopeLessonId: undefined,
        scopeLimitToLibrary: false,
        scopeLibraryBookId: undefined
      };
    }
    if (courseId != null && courseId > 0) {
      return {
        limitToLearningContext: true,
        scopeCourseId: courseId,
        scopeLevelId: undefined,
        scopeLessonId: undefined,
        scopeLimitToLibrary: false,
        scopeLibraryBookId: undefined
      };
    }
    return {
      limitToLearningContext: false,
      scopeCourseId: undefined,
      scopeLevelId: undefined,
      scopeLessonId: undefined,
      scopeLimitToLibrary: false,
      scopeLibraryBookId: undefined
    };
  }, [limitToContext, canLimitToLearningContext, learningScope]);

  useEffect(() => {
    if (!isOpen) return;
    if (debounced.length < 2) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const payload: LearnerSearchRequest = {
          query: debounced,
          searchCourseTitles: searchTitles,
          searchLevelTitles: searchTitles,
          searchLessonTitles: searchTitles,
          searchBlockTitles: searchTitles,
          searchCourseCatalog: searchDescriptions,
          searchLevelCatalog: searchDescriptions,
          searchTextBlocks: searchLessonText,
          searchLessonQuizzes,
          searchLessonExercises,
          searchLibraryBookTitles: searchTitles,
          searchLibraryBookText: searchLibraryBookText,
          ...scopePayload,
          searchVocabulary: true,
          searchLeitner: true
        };
        const hits = await contentService.searchLearnerContent(payload);
        if (!cancelled) setResults(hits);
      } catch (e) {
        if (!cancelled) {
          setResults([]);
          setError(e instanceof Error ? e.message : t('searchFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    debounced,
    isOpen,
    searchTitles,
    searchDescriptions,
    searchLessonText,
    searchLessonQuizzes,
    searchLessonExercises,
    searchLibraryBookText,
    scopePayload
  ]);

  useEffect(() => {
    if (!isOpen) {
      setAdvancedOpen(false);
      setQuery('');
      setDebounced('');
      setResults([]);
      setError(null);
      setSearchTitles(true);
      setSearchDescriptions(true);
      setSearchLessonText(true);
      setSearchLessonQuizzes(true);
      setSearchLessonExercises(true);
      setSearchLibraryBookText(true);
      setLimitToContext(false);
      setLockedModal(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!canLimitToLearningContext) setLimitToContext(false);
  }, [canLimitToLearningContext]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onFocus = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (detail?.targetKey === 'search-filters' || detail?.targetKey === 'search-scope') {
        setAdvancedOpen(true);
      }
    };
    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
  }, [isOpen]);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, onClose]);

  const mobileBottomOffset = useMemo(() => {
    if (audioBarPinned) {
      return mobilePinnedAudioChromeBottom(bannerCollapsed, audioBarExtraChromePx);
    }
    return bannerCollapsed ? '1rem' : '4.75rem';
  }, [bannerCollapsed, audioBarPinned, audioBarExtraChromePx]);

  const panelClasses = `learner-search-panel mme-tone fixed left-1/2 z-[58] box-border flex min-w-0 max-h-[min(52vh,480px)] flex-col overflow-x-hidden rounded-t-2xl border border-slate-300/90 bg-white shadow-[0_-12px_40px_-8px_rgba(15,23,42,0.22)] ring-1 ring-inset ring-slate-200/90 backdrop-blur-md transition-all duration-200 ease-out dark:border-gray-600 dark:bg-gray-900/95 dark:shadow-2xl dark:ring-0`;

  const panelStyle: React.CSSProperties = useMemo(() => {
    return {
      bottom: mobileBottomOffset,
      left: '50%',
      width: isMobile ? '90%' : 'min(28rem, 90vw)',
      maxWidth: '90vw',
      transform: presented ? 'translate(-50%, 0)' : 'translate(-50%, 1.5rem)',
      opacity: presented ? 1 : 0,
      pointerEvents: presented ? 'auto' : 'none'
    };
  }, [isMobile, mobileBottomOffset, presented]);

  const resultsListKey = `${debounced}-${results.length}`;

  const scopeDetailLabel = useMemo(() => {
    if (!canLimitToLearningContext) return null;
    const { lessonId, levelId, courseId, libraryBookId, limitToLibrary } = learningScope;
    if (libraryBookId != null && libraryBookId > 0) return t('scopeThisBook');
    if (limitToLibrary) return t('scopeLibrary');
    if (lessonId != null && lessonId > 0) return t('scopeCurrentLesson');
    if (levelId != null && levelId > 0) return t('scopeCurrentLevel');
    if (courseId != null && courseId > 0) return t('scopeCurrentCourse');
    return null;
  }, [canLimitToLearningContext, learningScope, t]);

  if (!mounted) return null;

  const panel = (
    <div
      className={panelClasses}
      style={panelStyle}
      role="dialog"
      aria-modal="true"
      aria-label={t('panelAriaLabel')}
    >
      <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-1 border-b border-slate-200 dark:border-gray-700 bg-slate-50/95 dark:bg-transparent"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <Search className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{t('title')}</h2>
        </div>
        <div className="flex shrink-0 flex-col items-center gap-0">
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-200/80 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
            aria-label={t('closeAriaLabel')}
          >
            <X className="w-4 h-4" />
          </button>
          <ModalTourHelpButton tourPageKey="search" />
        </div>
      </div>

      <p className="mme-hint text-[10px] leading-snug text-gray-600 dark:text-gray-400 px-3 pt-1.5 bg-slate-50/80 dark:bg-transparent">
        {t('hint')}
      </p>

      <div className="px-3 pt-2 flex flex-wrap items-center gap-2 bg-slate-50/80 dark:bg-transparent">
        <div
          data-tour-id="search-query"
          className="flex-1 min-w-[140px] flex items-center gap-1 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 shadow-sm"
        >
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="flex-1 min-w-0 flex items-center">
            <FarsiKeyboardInput
              value={query}
              onChange={setQuery}
              keyboardPosition="fixed-above-anchor"
              keyboardTriggerPlacement="outside-right"
              type="search"
              enterKeyHint="search"
              placeholder={t('queryPlaceholder')}
              className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 dark:text-gray-100 outline-none py-1 border-0 focus:ring-0"
              aria-label={t('queryAriaLabel')}
            />
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400 flex-shrink-0" />}
        </div>
        <button
          type="button"
          data-tour-id="search-advanced-toggle"
          onClick={() => setAdvancedOpen((o) => !o)}
          className={`flex items-center gap-1 text-xs font-medium px-2.5 py-2 rounded-xl border transition-colors ${
            advancedOpen
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200'
              : 'border-slate-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800'
          }`}
          title={advancedOpen ? t('hideAdvancedFilters') : t('chooseWhereToSearch')}
        >
          <SlidersHorizontal className="w-4 h-4" />
          {t('advanced')}
        </button>
      </div>

      <div className="overflow-hidden flex-shrink-0 bg-slate-50/80 dark:bg-transparent">
        <AnimatePresence initial={false}>
          {advancedOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0, y: -4 }}
              animate={{ height: 'auto', opacity: 1, y: 0 }}
              exit={{ height: 0, opacity: 0, y: -4 }}
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden"
            >
              <div className="mx-2 sm:mx-3 mt-1.5 mb-1.5 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 shadow-sm overflow-hidden min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-stretch divide-y sm:divide-y-0 sm:divide-x divide-slate-200 dark:divide-gray-600">
                  <div
                    data-tour-id="search-filters"
                    className="flex gap-2 items-center p-2 sm:p-2.5 flex-1 min-w-0 bg-slate-50/60 dark:bg-gray-900/20"
                  >
                    <div
                      className="flex flex-col items-center justify-center shrink-0 w-10 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 py-2 px-1 border border-blue-100/90 dark:border-blue-900/40"
                      aria-hidden
                    >
                      <ListFilter className="w-4 h-4" strokeWidth={2} />
                      <span className="text-[8px] font-semibold mt-1 leading-none text-center text-blue-700 dark:text-blue-300">
                        {t('matchLabel')}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 grid grid-cols-3 gap-x-2 sm:gap-x-3 gap-y-1.5 text-[10px] leading-tight items-center sm:grid-cols-3">
                      <label className="flex items-center gap-1.5 cursor-pointer select-none min-h-[1.25rem] min-w-0">
                        <input
                          type="checkbox"
                          checked={searchTitles}
                          onChange={() => setSearchTitles((v) => !v)}
                          className="rounded border-slate-300 dark:border-gray-500 shrink-0"
                        />
                        <span className="truncate">{t('filterTitles')}</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none min-h-[1.25rem] min-w-0">
                        <input
                          type="checkbox"
                          checked={searchDescriptions}
                          onChange={() => setSearchDescriptions((v) => !v)}
                          className="rounded border-slate-300 dark:border-gray-500 shrink-0"
                        />
                        <span className="truncate">{t('filterDescriptions')}</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none min-h-[1.25rem] min-w-0">
                        <input
                          type="checkbox"
                          checked={searchLessonText}
                          onChange={() => setSearchLessonText((v) => !v)}
                          className="rounded border-slate-300 dark:border-gray-500 shrink-0"
                        />
                        <span className="truncate">{t('filterLessonText')}</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none min-h-[1.25rem] min-w-0">
                        <input
                          type="checkbox"
                          checked={searchLessonQuizzes}
                          onChange={() => setSearchLessonQuizzes((v) => !v)}
                          className="rounded border-slate-300 dark:border-gray-500 shrink-0"
                        />
                        <span className="truncate">{t('filterQuiz')}</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none min-h-[1.25rem] min-w-0">
                        <input
                          type="checkbox"
                          checked={searchLessonExercises}
                          onChange={() => setSearchLessonExercises((v) => !v)}
                          className="rounded border-slate-300 dark:border-gray-500 shrink-0"
                        />
                        <span className="truncate">{t('filterExercise')}</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none min-h-[1.25rem] min-w-0">
                        <input
                          type="checkbox"
                          checked={searchLibraryBookText}
                          onChange={() => setSearchLibraryBookText((v) => !v)}
                          className="rounded border-slate-300 dark:border-gray-500 shrink-0"
                        />
                        <span className="truncate">{t('filterBookText')}</span>
                      </label>
                    </div>
                  </div>
                  <div
                    data-tour-id="search-scope"
                    className={`flex gap-2 items-center p-2 sm:p-2.5 sm:w-[min(11.5rem,42vw)] shrink-0 min-w-0 bg-violet-50/40 dark:bg-violet-950/15 ${
                      canLimitToLearningContext ? '' : 'opacity-65'
                    }`}
                  >
                    <div
                      className="flex flex-col items-center justify-center shrink-0 w-10 rounded-lg bg-violet-100/90 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300 py-2 px-1 border border-violet-200/90 dark:border-violet-800/50"
                      aria-hidden
                    >
                      <MapPin className="w-4 h-4" strokeWidth={2} />
                      <span className="text-[8px] font-semibold mt-1 leading-none text-center">{t('scopeLabel')}</span>
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                      <label
                        className={`flex items-start gap-2 select-none text-[10px] leading-tight text-gray-800 dark:text-gray-200 ${
                          canLimitToLearningContext ? 'cursor-pointer' : 'cursor-not-allowed'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={limitToContext && canLimitToLearningContext}
                          disabled={!canLimitToLearningContext}
                          onChange={() => {
                            if (canLimitToLearningContext) setLimitToContext((v) => !v);
                          }}
                          className="mt-0.5 rounded border-slate-300 dark:border-gray-500 shrink-0 disabled:opacity-50"
                        />
                        <span>
                          {t('limitToContext')}
                          {canLimitToLearningContext ? (
                            <span className="block font-normal text-[9px] text-slate-500 dark:text-gray-400 mt-0.5">
                              {t('limitToContextDetail', {
                                and: t('limitToContextAnd'),
                                scope: scopeDetailLabel ?? t('scopeFallback'),
                              })}
                            </span>
                          ) : null}
                        </span>
                      </label>
                      {!canLimitToLearningContext ? (
                        <p className="text-[9px] leading-snug text-slate-500 dark:text-gray-400 pl-6 sm:pl-0">
                          {t('limitToContextDisabledHint')}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div
        data-tour-id="search-results"
        className="flex-1 min-h-0 overflow-y-auto px-2 py-2 bg-slate-100/90 dark:bg-gray-950/20 border-t border-slate-100 dark:border-gray-800/80"
      >
        {error && <p className="text-xs text-red-600 dark:text-red-400 px-1 py-2">{error}</p>}
        {!loading && debounced.length >= 2 && results.length === 0 && !error && (
          <p className="text-xs text-gray-500 dark:text-gray-400 px-2 py-4 text-center">{t('noMatches')}</p>
        )}
        <AnimatePresence mode="popLayout">
          {results.length > 0 && (
            <motion.ul
              key={resultsListKey}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              className="space-y-1"
            >
              {results.map((r, idx) => (
                <motion.li
                  key={`${r.matchKind}-${r.leitnerCardId ?? ''}-${r.libraryBookId ?? 'c'}-${r.lessonId ?? ''}-${r.contentBlockId ?? ''}-${r.libraryBookBlockId ?? ''}-${r.lessonQuizId ?? ''}-${r.lessonExerciseId ?? ''}-${idx}`}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: Math.min(idx * 0.035, 0.35) }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      // Locked hits never navigate – open the unlock modal so the
                      // learner sees which plan they need.
                      if (r.isLocked) {
                        setLockedModal({
                          open: true,
                          title: r.label,
                          kind: (r.lockedAt ?? 'Course') as LockedContentKind,
                          plans: r.requiredPlans ?? []
                        });
                        return;
                      }
                      navigateFromResult(r, debounced);
                      onClose();
                    }}
                    className={`w-full text-left rounded-xl px-2.5 py-2 transition-colors border bg-white/80 dark:bg-gray-900/40 shadow-sm ${
                      r.isLocked
                        ? 'border-amber-200/80 dark:border-amber-800/50 hover:bg-amber-50/70 dark:hover:bg-amber-950/20'
                        : r.matchKind === 'Leitner'
                          ? 'border-rose-100 dark:border-rose-900/60 hover:border-rose-200 dark:hover:border-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30'
                          : 'border-transparent hover:border-blue-100 dark:hover:border-gray-600 hover:bg-blue-50 dark:hover:bg-gray-800/80'
                    }`}
                    title={r.isLocked ? t('lockedTapHint') : undefined}
                  >
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-xs font-medium text-gray-900 dark:text-gray-100 line-clamp-2 flex items-center gap-1">
                        {r.isLocked && (
                          <Lock
                            className="w-3 h-3 text-amber-600 dark:text-amber-400 flex-shrink-0"
                            aria-label={t('lockedAriaLabel')}
                          />
                        )}
                        {r.matchKind === 'Leitner' && (
                          <Bookmark className="w-3 h-3 text-rose-600 dark:text-rose-400 flex-shrink-0" />
                        )}
                        <span className="line-clamp-2">{highlightText(r.label, debounced)}</span>
                      </span>
                      <span className={`flex-shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                        r.matchKind === 'Leitner'
                          ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/70 dark:text-rose-100'
                          : 'bg-slate-200/90 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {matchKindLabel(r.matchKind, t)}
                      </span>
                    </div>
                    {r.snippet && (
                      <p className="text-[11px] text-gray-600 dark:text-gray-400 line-clamp-2">
                        {highlightText(r.snippet, debounced)}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-0.5 truncate">
                      {r.matchKind === 'Leitner'
                        ? t('matchKindLeitner')
                        : r.libraryBookTitle
                        ? r.libraryBookTitle
                        : [
                            r.courseTitle,
                            r.levelTitle,
                            r.lessonTitle
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                    </p>
                  </button>
                </motion.li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
      {/*
        The locked modal must escape this panel: the panel sets `backdrop-blur`
        and a `transform`, both of which create a containing block for any
        descendant `position: fixed` element. Without the portal, the modal
        would be clipped to the small panel surface instead of covering the
        viewport.
      */}
      {lockedModal?.open && typeof document !== 'undefined' &&
        createPortal(
          <ExerciseLockedModal
            open={lockedModal.open}
            onClose={() => setLockedModal(null)}
            exerciseTitle={lockedModal.title}
            requiredPlans={lockedModal.plans}
            contentKind={lockedModal.kind}
          />,
          document.body
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(panel, document.body) : panel;
};
